import { useState, useEffect, useRef } from "react";
import "./NotFound.css";
import sm_logo from "../assets/img/ruth-icon.png";

/* ── Star field ── */
const Stars = () => {
  const stars = Array.from({ length: 120 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 70}%`,
    size: Math.random() * 2.5 + 0.5,
    dur: `${2 + Math.random() * 4}s`,
    delay: `${Math.random() * 5}s`,
    op: 0.4 + Math.random() * 0.6,
  }));

  return (
    <div className="rn-stars">
      {stars.map((s) => (
        <div
          key={s.id}
          className="rn-star"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            "--dur": s.dur,
            "--delay": s.delay,
            "--op": s.op,
          }}
        />
      ))}
    </div>
  );
};

/* ── Ship SVG ── */
const ShipSVG = () => (
  <svg
    width="160"
    height="80"
    viewBox="0 0 160 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Hull */}
    <path d="M10 52 L150 52 L138 68 L22 68 Z" fill="#1a4aad" opacity="0.9" />
    {/* Hull highlight */}
    <path d="M10 52 L150 52 L145 56 L15 56 Z" fill="#2d7dd2" opacity="0.5" />
    {/* Deck */}
    <rect x="20" y="40" width="120" height="12" rx="2" fill="#0d2c6e" />
    {/* Bridge */}
    <rect x="55" y="20" width="50" height="20" rx="3" fill="#1a4aad" />
    <rect x="62" y="24" width="10" height="10" rx="1" fill="rgba(232,185,79,0.6)" />
    <rect x="78" y="24" width="10" height="10" rx="1" fill="rgba(232,185,79,0.6)" />
    <rect x="94" y="24" width="10" height="10" rx="1" fill="rgba(232,185,79,0.6)" />
    {/* Funnel */}
    <rect x="85" y="8" width="14" height="16" rx="3" fill="#0d2c6e" />
    <rect x="84" y="6" width="16" height="5" rx="2" fill="#e8b94f" />
    {/* Smoke */}
    <circle cx="92" cy="3" r="3" fill="rgba(255,255,255,0.15)" />
    <circle cx="96" cy="-1" r="2" fill="rgba(255,255,255,0.1)" />
    {/* Cargo */}
    <rect x="22" y="34" width="28" height="8" rx="1" fill="#e8b94f" opacity="0.8" />
    <rect x="52" y="34" width="2" height="8" fill="#0d2c6e" />
    {/* Mast */}
    <line x1="92" y1="8" x2="92" y2="28" stroke="#2d7dd2" strokeWidth="1.5" />
    {/* Flag */}
    <path d="M92 8 L104 12 L92 16 Z" fill="#e8b94f" opacity="0.9" />
    {/* Water line */}
    <path d="M10 52 Q80 60 150 52" stroke="rgba(45,125,210,0.3)" strokeWidth="1" fill="none" />
  </svg>
);

/* ── Wave layers ── */
const Waves = () => (
  <div className="rn-waves">
    <div className="rn-wave rn-wave-1">
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path
          d="M0,60 C240,110 480,10 720,60 C960,110 1200,10 1440,60 L1440,120 L0,120 Z"
          fill="rgba(13,44,110,0.6)"
        />
      </svg>
    </div>
    <div className="rn-wave rn-wave-2">
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path
          d="M0,40 C360,100 720,0 1080,60 C1260,90 1380,30 1440,40 L1440,120 L0,120 Z"
          fill="rgba(10,24,50,0.7)"
        />
      </svg>
    </div>
    <div className="rn-wave rn-wave-3">
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path
          d="M0,80 C180,20 360,100 540,60 C720,20 900,100 1080,70 C1260,40 1380,90 1440,80 L1440,120 L0,120 Z"
          fill="rgba(10,24,50,0.9)"
        />
      </svg>
    </div>
  </div>
);


/* ── Main component ── */
export default function NotFound() {
  const [countdown, setCountdown] = useState(10);
  const [showRedirect, setShowRedirect] = useState(true);
  const timerRef = useRef(null);

  const cancelTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setShowRedirect(false);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          window.location.href = "/dashboard";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const handleMouseMove = () => cancelTimer();
    window.addEventListener("mousemove", handleMouseMove, { once: true });

    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="rn-root">
      {/* ── Ocean Background ── */}
      <div className="rn-ocean">
        <Stars />
        <div className="rn-grid-overlay" />
        <div className="rn-glow rn-glow-1" />
        <div className="rn-glow rn-glow-2" />

        {/* Animated Ship */}
        <div className="rn-ship-scene">
          <div className="rn-ship-wrapper">
            <div className="rn-ship-bob">
              <ShipSVG />
            </div>
          </div>
        </div>

        <Waves />
      </div>

      {/* ── Page Content ── */}
      <div className="rn-page">
        {/* Logo */}
        <a href="/dashboard" className="rn-logo">
          <img src={sm_logo} alt="sm-logo" width={"35px"}/>
          <div className="rn-logo-text">
            
            <div className="rn-logo-name">
              ruth<strong>Shipping</strong>
            </div>
            <div className="rn-logo-tag">Ship Smarter</div>
          </div>
        </a>

        {/* 404 */}
        <div className="rn-error-num">404</div>

        {/* Decorative line */}
        <div className="rn-deco-line">
          <div className="rn-deco-diamond" />
        </div>

        {/* Messaging */}
        <h1 className="rn-error-title">
          Lost at <em>Sea</em>
        </h1>
        <p className="rn-error-sub">
          The page you're looking for has drifted off course. It may have been
          moved, renamed, or sailed away entirely. Let us navigate you back to
          safety.
        </p>

        {/* Buttons */}
        <div className="rn-btn-group">
          <a href="/dashboard" className="rn-btn-home" onClick={cancelTimer}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
            </svg>
            Return to Dashboard
          </a>
          <button
            className="rn-btn-back"
            onClick={() => {
              cancelTimer();
              window.history.back();
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Go Back
          </button>
        </div>

        {/* Countdown */}
        {showRedirect && (
          <div className="rn-redirect-note">
            Redirecting to dashboard in <span>{countdown}</span> seconds
          </div>
        )}

      </div>
    </div>
  );
}